import { useEffect, useMemo, useRef } from "react";
import { useClient } from "../../../../context/ClientContext";
import { Client, ClientNote } from "../../../../types/client";

/** Max notes (pinned + recent) shown inline before "+ n more not shown". */
export const MAX_NOTES_SHOWN = 2;

/**
 * The client notes an appointment preview shows: pinned (popup) notes plus
 * the most recent normal notes, capped at MAX_NOTES_SHOWN. Hydrates the
 * client into the ClientContext cache on open (at most once per client) so
 * the section and its count render without lag. Shared by the desktop
 * appointment drawer and the mobile appointment detail sheet.
 */
export function usePreviewClientNotes(clientId: string | undefined) {
  const {
    clientsById,
    refreshClientById,
    createOrUpdateClientNote,
    deleteClientNote,
  } = useClient() as {
    clientsById: Record<string, Client | undefined>;
    refreshClientById: (clientId: string) => Promise<void>;
    createOrUpdateClientNote: (
      clientId: string,
      noteData: { id?: string; text: string; is_popup?: boolean },
    ) => Promise<unknown>;
    deleteClientNote: (clientId: string, noteId: string) => Promise<unknown>;
  };

  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!clientId || hydratedRef.current === clientId) return;
    if (clientsById[clientId]?.notes) return; // already hydrated
    hydratedRef.current = clientId;
    void refreshClientById(clientId).catch(() => {});
  }, [clientId, clientsById, refreshClientById]);

  const cachedClient = clientId ? clientsById[clientId] : undefined;

  // Customer-provided booking notes live on the client profile as client
  // notes, so they surface here via cachedClient.notes.
  const cachedNotes = cachedClient?.notes;
  const notes: ClientNote[] = useMemo(() => cachedNotes ?? [], [cachedNotes]);

  const popupNotes = useMemo(
    () => notes.filter((n) => n.is_popup === true),
    [notes],
  );
  const pinnedCount = popupNotes.length;
  const recentNormalNotes = useMemo(() => {
    const normal = notes.filter((n) => !n.is_popup);
    normal.sort(
      (a, b) =>
        new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime(),
    );
    // Always show at least the single most recent note; otherwise fill up to
    // MAX_NOTES_SHOWN once the pinned/popup notes are accounted for.
    const slots = Math.max(1, MAX_NOTES_SHOWN - pinnedCount);
    return normal.slice(0, slots);
  }, [notes, pinnedCount]);

  const hiddenNormalCount =
    notes.filter((n) => !n.is_popup).length - recentNormalNotes.length;
  const notesCount = cachedClient?.notes?.length;

  // Quick pin/unpin a client note (toggles is_popup). createOrUpdateClientNote
  // updates the ClientContext cache, so the card re-renders into the right
  // pinned/normal group automatically.
  const togglePin = (n: ClientNote) => {
    if (!clientId) return;
    void createOrUpdateClientNote(clientId, {
      id: n.id,
      text: n.text,
      is_popup: !n.is_popup,
    }).catch((err) => console.error("Could not update note pin state:", err));
  };

  // Notes nested screen (appointment drawer): add / edit / delete through the
  // same ClientContext calls the client profile uses (they update the cache).
  const saveNote = async (note: {
    id?: string;
    text: string;
    is_popup?: boolean;
  }) => {
    if (!clientId) return;
    await createOrUpdateClientNote(clientId, note);
  };
  const removeNote = async (noteId: string) => {
    if (!clientId) return;
    await deleteClientNote(clientId, noteId);
  };

  return {
    cachedClient,
    notes,
    popupNotes,
    recentNormalNotes,
    hiddenNormalCount,
    notesCount,
    togglePin,
    saveNote,
    removeNote,
  };
}
